import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Save, Plus, Loader2, Trash2, Edit2,
  Clock, CalendarDays, ChevronUp, ChevronDown, Zap, ListOrdered
} from "lucide-react";
import {
  api, ProjectTemplate, ProjectRole, TaskSection, TaskTemplate,
  TaskSectionInput, TaskTemplateInput, WorkflowPipeline, Tag, User
} from "@/lib/api";
import { TagChip, TagPicker } from "@/components/custom/TagPicker";
import { SectionAutomationsModal } from "@/components/custom/SectionAutomationsModal";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";

const dateConstraintLabels: Record<string, string> = {
  from_start: "Days from start",
  before_due: "Days before due",
  // FC imported "Days before due" under this name; the server treats it as before_due.
  due_date: "Days before due",
};

// Blank Days = no due date (ENG-125). Never coerce an empty field to 0.
const parseRelativeDays = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
};

export default function TemplateEdit() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);
  const headerAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerInitialized = useRef(false);
  const headerFormTemplateId = useRef<string | null>(null); // prevents refetch from overwriting user edits

  const [headerForm, setHeaderForm] = useState({
    title: "",
    description: "",
    default_duration_days: undefined as number | undefined,
    recurring_pattern: undefined as string | undefined,
    default_billing_rate: undefined as number | undefined,
    is_active: true,
    workflow_pipeline_id: undefined as string | undefined,
    project_type: undefined as string | undefined,
  });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskTemplate | null>(null);
  const [taskForm, setTaskForm] = useState<Partial<TaskTemplateInput>>({});
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ name: "", is_client_facing: false, is_sequential: false });
  const [sectionRulesOpen, setSectionRulesOpen] = useState<string | null>(null);
  const [taskRulesOpen, setTaskRulesOpen] = useState<string | null>(null);

  const { data: template, isLoading } = useQuery<ProjectTemplate>({
    queryKey: ["projectTemplate", params.id],
    queryFn: () => api.getProjectTemplate(params.id!),
    enabled: !!params.id,
  });

  const { data: roles = [] } = useQuery<ProjectRole[]>({
    queryKey: ["projectRoles"],
    queryFn: () => api.getProjectRoles(),
  });

  const { data: workflowPipelines = [] } = useQuery<WorkflowPipeline[]>({
    queryKey: ["workflow-pipelines"],
    queryFn: () => api.getWorkflowPipelines(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: defaultTags = [] } = useQuery<Tag[]>({
    queryKey: ["template-default-tags", params.id],
    queryFn: () => api.getTemplateDefaultTags(params.id!),
    enabled: !!params.id,
  });

  const { data: staffUsers = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    staleTime: 5 * 60 * 1000,
  });

  const addDefaultTagMutation = useMutation({
    mutationFn: (tagId: string) => api.addTemplateDefaultTag(params.id!, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["template-default-tags", params.id] }),
  });

  const removeDefaultTagMutation = useMutation({
    mutationFn: (tagId: string) => api.removeTemplateDefaultTag(params.id!, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["template-default-tags", params.id] }),
  });

  useEffect(() => {
    // Only initialize once per template ID — subsequent refetches must not overwrite user edits
    if (template && headerFormTemplateId.current !== template.id) {
      setHeaderForm({
        title: template.title,
        description: template.description || "",
        default_duration_days: template.default_duration_days || undefined,
        recurring_pattern: template.recurring_pattern || undefined,
        default_billing_rate: template.default_billing_rate ? parseFloat(template.default_billing_rate) : undefined,
        is_active: template.is_active,
        workflow_pipeline_id: template.workflow_pipeline_id || undefined,
        project_type: template.project_type || undefined,
      });
      headerFormTemplateId.current = template.id;
      headerInitialized.current = true;
    }
  }, [template]);

  const updateTemplateMutation = useMutation({
    mutationFn: (data: Partial<typeof headerForm>) => 
      api.updateProjectTemplate(params.id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      queryClient.invalidateQueries({ queryKey: ["project-templates"] });
      queryClient.invalidateQueries({ queryKey: ["projectTemplates"] });
      toast({ title: "Template updated" });
      setIsSaving(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setIsSaving(false);
    },
  });

  const createSectionMutation = useMutation({
    mutationFn: (data: TaskSectionInput) => 
      api.createTaskSection(params.id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      toast({ title: "Section added" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (id: string) => api.deleteTaskSection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      toast({ title: "Section deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: ({ sectionId, data }: { sectionId: string; data: TaskTemplateInput }) => 
      api.createTaskTemplate(sectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      toast({ title: "Task added" });
      closeTaskDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TaskTemplateInput> }) => 
      api.updateTaskTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      toast({ title: "Task updated" });
      closeTaskDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => api.deleteTaskTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      toast({ title: "Task deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const assignTaskMutation = useMutation({
    mutationFn: ({ id, assigned_user_id }: { id: string; assigned_user_id: string | null }) =>
      api.updateTaskTemplate(id, { assigned_user_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
    },
  });

  // Auto-save header 800ms after last change — silent (no toast, no spinner)
  useEffect(() => {
    if (!headerInitialized.current || !headerForm.title?.trim()) return;
    if (headerAutoSaveRef.current) clearTimeout(headerAutoSaveRef.current);
    headerAutoSaveRef.current = setTimeout(async () => {
      try {
        await api.updateProjectTemplate(params.id!, headerForm);
        queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
        queryClient.invalidateQueries({ queryKey: ["project-templates"] });
        queryClient.invalidateQueries({ queryKey: ["projectTemplates"] });
      } catch { /* silent */ }
    }, 800);
    return () => { if (headerAutoSaveRef.current) clearTimeout(headerAutoSaveRef.current); };
  }, [headerForm]);

  // Auto-save task form 800ms after last change when editing an existing task — silent
  useEffect(() => {
    if (!editingTask || !taskForm.title?.trim()) return;
    if (taskAutoSaveRef.current) clearTimeout(taskAutoSaveRef.current);
    taskAutoSaveRef.current = setTimeout(async () => {
      try {
        await api.updateTaskTemplate(editingTask.id, taskForm);
        queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
      } catch { /* silent */ }
    }, 800);
    return () => { if (taskAutoSaveRef.current) clearTimeout(taskAutoSaveRef.current); };
  }, [taskForm]);

  const handleSaveHeader = () => {
    if (!headerForm.title.trim()) {
      toast({ title: "Error", description: "Title is required", variant: "destructive" });
      return;
    }
    setIsSaving(true);
    updateTemplateMutation.mutate(headerForm);
  };

  const openAddTaskDialog = (sectionId: string) => {
    setEditingTask(null);
    setSelectedSectionId(sectionId);
    setTaskForm({
      title: "",
      description: "",
      template_date_constraint: "from_start",
      relative_days: null,
      is_client_task: false,
      allow_client_upload: false,
      budgeted_minutes: undefined,
      project_role_id: undefined,
    });
    setTaskDialogOpen(true);
  };

  const openEditTaskDialog = (task: TaskTemplate) => {
    setEditingTask(task);
    setSelectedSectionId(task.section_id || null);
    setTaskForm({
      title: task.title,
      description: task.description || "",
      template_date_constraint: task.template_date_constraint === "due_date" ? "before_due" : (task.template_date_constraint || "from_start"),
      relative_days: task.relative_days ?? null,
      is_client_task: task.is_client_task,
      allow_client_upload: task.allow_client_upload,
      budgeted_minutes: task.budgeted_minutes || undefined,
      project_role_id: task.project_role_id || undefined,
    });
    setTaskDialogOpen(true);
  };

  const closeTaskDialog = () => {
    setTaskDialogOpen(false);
    setEditingTask(null);
    setSelectedSectionId(null);
    setTaskForm({});
  };

  const handleTaskSubmit = () => {
    if (!taskForm.title?.trim()) {
      toast({ title: "Error", description: "Task title is required", variant: "destructive" });
      return;
    }

    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data: taskForm });
    } else if (selectedSectionId) {
      createTaskMutation.mutate({
        sectionId: selectedSectionId,
        data: {
          project_template_id: params.id!,
          title: taskForm.title!,
          description: taskForm.description,
          template_date_constraint: taskForm.template_date_constraint,
          relative_days: taskForm.relative_days,
          is_client_task: taskForm.is_client_task,
          allow_client_upload: taskForm.allow_client_upload,
          budgeted_minutes: taskForm.budgeted_minutes,
          project_role_id: taskForm.project_role_id,
          list_index: (template?.tasks || []).filter(t => t.section_id === selectedSectionId).length,
        },
      });
    }
  };

  const openSectionDialog = () => {
    setSectionForm({ name: "", is_client_facing: false, is_sequential: false });
    setSectionDialogOpen(true);
  };

  const handleAddSection = () => {
    if (!sectionForm.name.trim()) {
      toast({ title: "Error", description: "Section name is required", variant: "destructive" });
      return;
    }
    createSectionMutation.mutate({
      name: sectionForm.name.trim(),
      section_type: sectionForm.is_client_facing ? "client_tasks" : "firm_tasks",
      is_client_facing: sectionForm.is_client_facing,
      list_index: (template?.sections || []).length,
      is_sequential: sectionForm.is_sequential,
    });
    setSectionDialogOpen(false);
    setSectionForm({ name: "", is_client_facing: false, is_sequential: false });
  };

  // Toggle "tasks must be done in order" on an existing template section.
  // Projects created from the template inherit the flag.
  const handleToggleSequential = async (sectionId: string, current: boolean) => {
    try {
      await api.updateTaskSection(sectionId, { is_sequential: !current });
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
    } catch (err: any) {
      toast({ title: "Error updating section ordering", description: err.message, variant: "destructive" });
    }
  };

  const moveTask = async (task: TaskTemplate, direction: "up" | "down") => {
    const sectionTasks = getTasksForSection(task.section_id || "");
    const currentIndex = sectionTasks.findIndex(t => t.id === task.id);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sectionTasks.length) return;
    const otherTask = sectionTasks[newIndex];
    try {
      await Promise.all([
        api.updateTaskTemplate(task.id, { list_index: otherTask.list_index }),
        api.updateTaskTemplate(otherTask.id, { list_index: task.list_index }),
      ]);
      queryClient.invalidateQueries({ queryKey: ["projectTemplate", params.id] });
    } catch (err: any) {
      toast({ title: "Error reordering task", description: err.message, variant: "destructive" });
    }
  };

  const getTasksForSection = (sectionId: string) => {
    return (template?.tasks || []).filter(t => t.section_id === sectionId);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!template) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Template not found</p>
          <Button variant="link" onClick={() => navigate("/templates")}>
            Back to Templates
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/templates")} data-testid="button-back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{template.title}</h1>
              <p className="text-muted-foreground text-sm">Edit template settings and tasks</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={template.is_active ? "default" : "secondary"}>
              {template.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Template Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title *</Label>
                  <Input
                    id="edit-title"
                    value={headerForm.title}
                    onChange={(e) => setHeaderForm({ ...headerForm, title: e.target.value })}
                    data-testid="input-edit-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={headerForm.description || ""}
                    onChange={(e) => setHeaderForm({ ...headerForm, description: e.target.value })}
                    rows={3}
                    data-testid="input-edit-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-duration">Default Duration (days)</Label>
                  <Input
                    id="edit-duration"
                    type="number"
                    min={1}
                    value={headerForm.default_duration_days || ""}
                    onChange={(e) => setHeaderForm({ 
                      ...headerForm, 
                      default_duration_days: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                    data-testid="input-edit-duration"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-recurring">Recurrence</Label>
                  <Select 
                    value={headerForm.recurring_pattern || "none"} 
                    onValueChange={(value) => setHeaderForm({ 
                      ...headerForm, 
                      recurring_pattern: value === "none" ? undefined : value 
                    })}
                  >
                    <SelectTrigger data-testid="select-edit-recurring">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">One-time</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-billing-rate">Default Billing Rate ($/hr)</Label>
                  <Input
                    id="edit-billing-rate"
                    type="number"
                    min={0}
                    step={0.01}
                    value={headerForm.default_billing_rate || ""}
                    onChange={(e) => setHeaderForm({ 
                      ...headerForm, 
                      default_billing_rate: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    data-testid="input-edit-billing-rate"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-pipeline">Workflow Pipeline</Label>
                  <Select
                    value={headerForm.workflow_pipeline_id || "none"}
                    onValueChange={(value) => setHeaderForm({
                      ...headerForm,
                      workflow_pipeline_id: value === "none" ? undefined : value,
                    })}
                  >
                    <SelectTrigger id="edit-pipeline">
                      <SelectValue placeholder="No pipeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No pipeline</SelectItem>
                      {workflowPipelines.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Projects created from this template will use this pipeline's stages.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-project-type">Project Type</Label>
                  <Select
                    value={headerForm.project_type || "none"}
                    onValueChange={(value) => setHeaderForm({ ...headerForm, project_type: value === "none" ? undefined : value })}
                  >
                    <SelectTrigger id="edit-project-type">
                      <SelectValue placeholder="— None —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="1040">1040</SelectItem>
                      <SelectItem value="1120S">1120S</SelectItem>
                      <SelectItem value="1065">1065</SelectItem>
                      <SelectItem value="1120">1120</SelectItem>
                      <SelectItem value="1041">1041</SelectItem>
                      <SelectItem value="990">990</SelectItem>
                      <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
                      <SelectItem value="Financial Statements">Financial Statements</SelectItem>
                      <SelectItem value="Property Tax">Property Tax</SelectItem>
                      <SelectItem value="Sales Tax">Sales Tax</SelectItem>
                      <SelectItem value="Payroll">Payroll</SelectItem>
                      <SelectItem value="Advisory">Advisory</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Projects created from this template will inherit this type.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Default Tags</Label>
                  <div className="flex flex-wrap items-center gap-1 min-h-[28px]">
                    {defaultTags.map((tag) => (
                      <TagChip
                        key={tag.id}
                        tag={tag}
                        onRemove={() => removeDefaultTagMutation.mutate(tag.id)}
                      />
                    ))}
                    <TagPicker
                      appliedTags={defaultTags}
                      onAdd={(tagId) => addDefaultTagMutation.mutate(tagId)}
                      onRemove={(tagId) => removeDefaultTagMutation.mutate(tagId)}
                      align="start"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    These tags are automatically applied to every project created from this template.
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-active">Active</Label>
                  <Switch
                    id="edit-active"
                    checked={headerForm.is_active}
                    onCheckedChange={(checked) => setHeaderForm({ ...headerForm, is_active: checked })}
                    data-testid="switch-edit-active"
                  />
                </div>

                <Separator />

                <Button 
                  className="w-full" 
                  onClick={handleSaveHeader}
                  disabled={isSaving}
                  data-testid="button-save-settings"
                >
                  {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Task Builder</CardTitle>
                <Button variant="outline" size="sm" onClick={openSectionDialog} data-testid="button-add-section">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Section
                </Button>
              </CardHeader>
              <CardContent>
                {(template.sections || []).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No sections yet. Add a section to start building tasks.
                  </div>
                ) : (
                  <Accordion type="multiple" defaultValue={template.sections?.map(s => s.id) || []} className="space-y-2">
                    {(template.sections || []).map((section) => (
                      <AccordionItem key={section.id} value={section.id} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{section.name}</span>
                              <button
                                type="button"
                                title={section.is_sequential ? "Tasks must be done in order — click for any order" : "Tasks may be done in any order — click to require order (notifies the next assignee as each task completes)"}
                                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border font-bold shadow-sm transition-colors shrink-0 ${section.is_sequential ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700" : "border-amber-500 bg-amber-400 text-amber-950 hover:bg-amber-300"}`}
                                onClick={e => { e.stopPropagation(); handleToggleSequential(section.id, !!section.is_sequential); }}
                              >
                                <ListOrdered className="w-3.5 h-3.5" />
                                Ordered & Notified? {section.is_sequential ? "(Yes)" : "(No)"}
                              </button>
                              {section.is_client_facing && (
                                <Badge variant="outline" className="text-xs">Client Facing</Badge>
                              )}
                              <Badge variant="secondary" className="text-xs">
                                {getTasksForSection(section.id).length} tasks
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                title="Section automation rules"
                                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-transparent hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 text-muted-foreground transition-colors"
                                onClick={e => { e.stopPropagation(); setSectionRulesOpen(section.id); }}
                              >
                                <Zap className="w-3 h-3" />
                                Automations
                              </button>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 pt-2">
                            {getTasksForSection(section.id).map((task, idx, arr) => (
                              <div
                                key={task.id}
                                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group"
                              >
                                <div className="flex flex-col gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0"
                                    disabled={idx === 0}
                                    onClick={() => moveTask(task, "up")}
                                    data-testid={`button-move-up-${task.id}`}
                                  >
                                    <ChevronUp className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0"
                                    disabled={idx === arr.length - 1}
                                    onClick={() => moveTask(task, "down")}
                                    data-testid={`button-move-down-${task.id}`}
                                  >
                                    <ChevronDown className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">{task.title}</div>
                                  <div className="text-xs text-muted-foreground flex items-center gap-3 mt-1">
                                    {task.budgeted_minutes && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {Math.floor(task.budgeted_minutes / 60)}h {task.budgeted_minutes % 60}m
                                      </span>
                                    )}
                                    {task.relative_days !== null && task.relative_days !== undefined && (
                                      <span className="flex items-center gap-1">
                                        <CalendarDays className="h-3 w-3" />
                                        {dateConstraintLabels[task.template_date_constraint || "from_start"]}: {task.relative_days}
                                      </span>
                                    )}
                                    {task.is_client_task && (
                                      <Badge variant="outline" className="text-xs">Client</Badge>
                                    )}
                                    <select
                                      className="text-xs bg-transparent border border-border rounded px-1 py-0.5 text-foreground cursor-pointer"
                                      value={task.assigned_user_id ?? ""}
                                      onChange={(e) => assignTaskMutation.mutate({
                                        id: task.id,
                                        assigned_user_id: e.target.value || null,
                                      })}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="">Unassigned</option>
                                      {staffUsers.map((u) => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    title="Task automation rules"
                                    className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-transparent hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700 text-muted-foreground transition-colors"
                                    onClick={e => { e.stopPropagation(); setTaskRulesOpen(task.id); }}
                                  >
                                    <Zap className="w-3 h-3" />
                                    Automations
                                  </button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => openEditTaskDialog(task)}
                                    data-testid={`button-edit-task-${task.id}`}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={async () => {
                                      if (await askConfirm({
                                        title: "Delete this template task?",
                                        subject: "template task",
                                        name: task.title,
                                        body: "will be deleted from the template.",
                                        lines: ["Projects already created from this template keep their copy of it."],
                                        confirmLabel: "Delete",
                                        destructive: true,
                                      })) {
                                        deleteTaskMutation.mutate(task.id);
                                      }
                                    }}
                                    data-testid={`button-delete-task-${task.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full mt-2"
                              onClick={() => openAddTaskDialog(section.id)}
                              data-testid={`button-add-task-${section.id}`}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Task
                            </Button>
                          </div>
                          <div className="flex justify-end pt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={async () => {
                                if (await askConfirm({
                                  title: "Delete this template section?",
                                  subject: "template section",
                                  name: section.name,
                                  body: "will be deleted from the template.",
                                  lines: ["Every task in it is deleted with it."],
                                  confirmLabel: "Delete",
                                  destructive: true,
                                })) {
                                  deleteSectionMutation.mutate(section.id);
                                }
                              }}
                              data-testid={`button-delete-section-${section.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Section
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Section Automation Rules Modal */}
      <SectionAutomationsModal
        sectionId={sectionRulesOpen}
        onClose={() => setSectionRulesOpen(null)}
        pipelineId={headerForm.workflow_pipeline_id}
      />
      <SectionAutomationsModal
        sectionId={null}
        templateTaskId={taskRulesOpen}
        onClose={() => setTaskRulesOpen(null)}
        pipelineId={headerForm.workflow_pipeline_id}
      />

      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? "Edit Task" : "Add Task"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title || ""}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="e.g., Gather client documents"
                data-testid="input-task-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={taskForm.description || ""}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                rows={2}
                data-testid="input-task-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-constraint">Date Constraint</Label>
                <Select 
                  value={taskForm.template_date_constraint || "from_start"} 
                  onValueChange={(value) => setTaskForm({ ...taskForm, template_date_constraint: value })}
                >
                  <SelectTrigger data-testid="select-task-constraint">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="from_start">Days from start</SelectItem>
                    <SelectItem value="before_due">Days before due</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-days">Days</Label>
                <Input
                  id="task-days"
                  type="number"
                  min={0}
                  placeholder="No due date"
                  value={taskForm.relative_days ?? ""}
                  onChange={(e) => setTaskForm({ ...taskForm, relative_days: parseRelativeDays(e.target.value) })}
                  data-testid="input-task-days"
                />
                <p className="text-xs text-muted-foreground">Leave blank for no due date.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task-budget">Budgeted Time (minutes)</Label>
                <Input
                  id="task-budget"
                  type="number"
                  min={0}
                  value={taskForm.budgeted_minutes || ""}
                  onChange={(e) => setTaskForm({ 
                    ...taskForm, 
                    budgeted_minutes: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                  placeholder="60"
                  data-testid="input-task-budget"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="task-role">Assign to Role</Label>
                <Select 
                  value={taskForm.project_role_id || "none"} 
                  onValueChange={(value) => setTaskForm({ 
                    ...taskForm, 
                    project_role_id: value === "none" ? undefined : value 
                  })}
                >
                  <SelectTrigger data-testid="select-task-role">
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No role</SelectItem>
                    {roles.filter((role) => role.id).map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="task-client">Client Task</Label>
                <p className="text-xs text-muted-foreground">Visible to clients</p>
              </div>
              <Switch
                id="task-client"
                checked={taskForm.is_client_task || false}
                onCheckedChange={(checked) => setTaskForm({ ...taskForm, is_client_task: checked })}
                data-testid="switch-task-client"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="task-upload">Allow Client Upload</Label>
                <p className="text-xs text-muted-foreground">Client can upload files</p>
              </div>
              <Switch
                id="task-upload"
                checked={taskForm.allow_client_upload || false}
                onCheckedChange={(checked) => setTaskForm({ ...taskForm, allow_client_upload: checked })}
                data-testid="switch-task-upload"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeTaskDialog} data-testid="button-cancel-task">
              Cancel
            </Button>
            <Button
              onClick={handleTaskSubmit}
              disabled={createTaskMutation.isPending || updateTaskMutation.isPending}
              data-testid="button-save-task"
            >
              {(createTaskMutation.isPending || updateTaskMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingTask ? "Update Task" : "Add Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="section-name">Section Name *</Label>
              <Input
                id="section-name"
                value={sectionForm.name}
                onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                placeholder="e.g., Preparation Tasks"
                data-testid="input-section-name"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="section-client-facing">Client Facing</Label>
                <p className="text-xs text-muted-foreground">Tasks visible to clients</p>
              </div>
              <Switch
                id="section-client-facing"
                checked={sectionForm.is_client_facing}
                onCheckedChange={(checked) => setSectionForm({ ...sectionForm, is_client_facing: checked })}
                data-testid="switch-section-client-facing"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="section-sequential">Tasks must be done in order</Label>
                <p className="text-xs text-muted-foreground">Each task waits on the one above it; completing a task notifies the next assignee</p>
              </div>
              <Switch
                id="section-sequential"
                checked={sectionForm.is_sequential}
                onCheckedChange={(checked) => setSectionForm({ ...sectionForm, is_sequential: checked })}
                data-testid="switch-section-sequential"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialogOpen(false)} data-testid="button-cancel-section">
              Cancel
            </Button>
            <Button
              onClick={handleAddSection}
              disabled={createSectionMutation.isPending}
              data-testid="button-save-section"
            >
              {createSectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
